import { timeNow } from '../utils/clock';
import { formatTime, parseCallSign } from '../utils/dataUtils';
import DescriptionEdit from './DescriptionEdit';
import { TagsComponent } from './TagsComponent';
import { $defaultFrequency, $myCallsign } from '@/stores';
import { logs } from '@/stores/logbook';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import {
  type Dispatch,
  type KeyboardEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

export default function CreateLogComponent({
  id,
  className,
  frequency,
  setFrequency,
  callSign,
  setCallSign,
  description,
  setDescription,
  descriptionPlaceholder,
  time,
  setTime,
  tagIds,
  setTagIds,
  save,
  saveButton,
  callSignRef,
}: {
  id?: string;
  className?: string;
  frequency: string;
  setFrequency: Dispatch<SetStateAction<string>>;
  callSign: string;
  setCallSign: Dispatch<SetStateAction<string>>;
  description: string;
  setDescription: Dispatch<SetStateAction<string>>;
  descriptionPlaceholder?: string;
  time: string;
  setTime: Dispatch<SetStateAction<string>>;
  tagIds: string[];
  setTagIds: Dispatch<SetStateAction<string[]>>;
  save: () => void;
  saveButton: React.ReactNode;
  callSignRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const myCallsign = useStore($myCallsign);
  const defaultFrequency = useStore($defaultFrequency);

  const [timePlaceholder, setTimePlaceholder] = useState(() =>
    formatTime(timeNow()),
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        save();
      }
    },
    [save],
  );

  useEffect(() => {
    // Update time placeholder every minute on the minute
    const updateTimePlaceholder = () => {
      setTimePlaceholder(formatTime(timeNow()));
    };

    const now = new Date();
    const delay = (59 - now.getSeconds()) * 1000 + now.getMilliseconds();
    const timeoutId = setTimeout(() => {
      updateTimePlaceholder();
      const intervalId = setInterval(updateTimePlaceholder, 60000);
      return () => clearInterval(intervalId);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <tr id={id} className={clsx('create-log print:hidden', className)}>
      <td className="border border-inherit text-nowrap">
        <input
          list="frequencies"
          type="text"
          value={frequency}
          onChange={e => setFrequency(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={defaultFrequency}
          className="create-log-frequency w-full py-2 text-center"
        />
        <datalist className="frequencies">
          {[...new Set(logs.getFrequencies())].map(freq => (
            <option key={freq} value={freq} />
          ))}
        </datalist>
      </td>
      <td className="border border-inherit text-center text-nowrap">
        <input
          ref={callSignRef}
          type="text"
          value={callSign}
          onChange={e => setCallSign(e.target.value)}
          className="create-log-callsign w-full px-2 py-2 text-center"
          onKeyDown={handleKeyDown}
          onBlur={() => {
            const parsed = parseCallSign(callSign, myCallsign);
            if (!parsed || (parsed.from === '' && parsed.to === '')) return;
            setCallSign(`${parsed.from} > ${parsed.to}`);
          }}
        />
      </td>
      <td className="border border-inherit text-center">
        <input
          type="text"
          value={time}
          onChange={e => setTime(e.target.value)}
          className="create-log-time w-full px-2 py-2 text-center"
          placeholder={timePlaceholder}
          onKeyDown={handleKeyDown}
        />
      </td>
      <td className="create-log-description border border-inherit">
        <DescriptionEdit
          description={description}
          setDescription={setDescription}
          placeholder={descriptionPlaceholder}
          onSave={save}
        />
      </td>
      <td className="create-log-save border border-inherit text-center">
        {saveButton}
      </td>
      <td className="create-log-tags border border-inherit text-center">
        <TagsComponent
          tagIds={tagIds}
          addTag={tag => {
            if (!tagIds.includes(tag.id)) {
              setTagIds([...tagIds, tag.id]);
            }
          }}
          removeTag={tagId => {
            setTagIds(tagIds.filter(id => id !== tagId));
          }}
        />
      </td>
    </tr>
  );
}
